import { useEffect, useRef, useState } from "react";
import Summary from "./Cards/Summary";
import ReportDownload from "./Cards/ReportDownload";
import Advertisment from "./Cards/Advertisment";
import {
  useParams,
  useSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import PredictorResultCard from "./Cards/PredictorResultCard";
import Select, { StylesConfig } from "react-select";
import { CollegesData, College, Course, CutOff } from "../../util/types";
import PredictedRankCard from "./components/PredictedRankCard";
import { EpRight } from "../../components/study-abroad-landing/AboutEducationMentorsGlobally";
import TopCollegesUniversities from "../college-predictor-details/components/TopCollegesUniversities";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
interface IFilterOption {
  name: string;
  count: number;
  key: string;
}

interface IFilter {
  options: IFilterOption[];
  categoryKey: string;
  categoryName: string;
}

interface ICollegeCourse {
  gender: string;
  caste: string;
  counsellingName: string;
  courseTitle: string;
  lastYearCutoffAiRank: number;
  openingRank: number;
  closingRank: number;
  courseName: string;
  collegeId: number;
  fee: string;
  seats: number;
  year: string;
}

interface ICollege {
  cityName: string;
  stateName: string;
  collegeName: string;
  ownerShip: string;
  collegeId: number;
  collegeTitle: string;
  courseVo: ICollegeCourse[];
  allCourses: ICollegeCourse[];
}

interface IResponseData {
  toughChanceColleges: number;
  filter: IFilter[];
  goodChanceColleges: number;
  colleges: ICollege[];
  getinChanceColleges: number;
  totalCount: number;
}

interface FormData {
  counselling: string;
  state: string;
  caste: string;
  gender: string;
  rank: string;
  mobileNumber: string;
}

const ITEMS_PER_PAGE = 5;

const CollegePredictorResult = () => {
  const listingContainerRef = useRef<HTMLDivElement>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [allColleges, setAllColleges] = useState<ICollege[]>([]);
  const [filteredColleges, setFilteredColleges] = useState<ICollege[]>([]);
  const [ownershipFilter, setOwnershipFilter] = useState<string>("");
  const [collegeFilter, setCollegeFilter] = useState<string>("");
  const [courseFilter, setCourseFilter] = useState<string>("");
  const [cityFilter, setCityFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { exam: examName, rank } = useParams<{ exam: string; rank: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const counselling = searchParams.get("counselling") || "";
  const state = searchParams.get("state") || "";
  const caste = searchParams.get("caste") || "";
  const gender = searchParams.get("gender") || "";

  const { collegesData, formData } =
    (location.state as {
      collegesData: IResponseData;
      formData: FormData;
    }) || {};
  // Function to get the latest year for a single college
  const getLatestYear = (college: ICollege) => {
    const allYears = college.courseVo
      .map((course) => parseInt(course.year))
      .filter((year) => !isNaN(year));
    return allYears.length > 0 ? Math.max(...allYears).toString() : "2024";
  };

  // ... (rest of the component code until the useEffect hook)

  useEffect(() => {
    let data: IResponseData | null = collegesData;
    

    if (!data?.colleges) {
      return;
    }

    const colleges = data.colleges;

    // Apply form filters
    let filtered = colleges
      .map((college) => {
        // Get the latest year for this specific college
        const latestYear = getLatestYear(college);
        return {
          ...college,
          courseVo: college.courseVo
            .map((course) => ({
              ...course,
              gender:
                course.gender === "" ? "Neutral" : course.gender || "Neutral",
            }))
            .filter(
              (course) =>
                course.year === latestYear &&
                (counselling
                  ? course.counsellingName?.toLowerCase() ===
                    counselling.toLowerCase()
                  : true) &&
                (state
                  ? college.stateName?.toLowerCase() === state.toLowerCase()
                  : true) &&
                (caste ? course.caste === caste : true) &&
                (gender ? course.gender === gender : true)
            ),
          allCourses: college.allCourses.map((course) => ({
            ...course,
            gender:
              course.gender === "" ? "Neutral" : course.gender || "Neutral",
          })),
        };
      })
      .filter((college) => college.courseVo.length > 0);

    // Apply additional filters
    if (ownershipFilter) {
      filtered = filtered.filter(
        (college) => college.ownerShip === ownershipFilter
      );
    }
    if (cityFilter) {
      filtered = filtered.filter(
        (college) =>
          college.cityName?.toLowerCase() === cityFilter.toLowerCase()
      );
    }
    if (searchQuery) {
      filtered = filtered.filter((college) =>
        college.collegeName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (collegeFilter) {
      filtered = filtered.filter(
        (college) => college.collegeTitle === collegeFilter
      );
    }
    if (courseFilter) {
      filtered = filtered
        .map((college) => ({
          ...college,
          courseVo: college.courseVo.filter(
            (course) => course.courseTitle === courseFilter
          ),
        }))
        .filter((college) => college.courseVo.length > 0);
    }

    // Deduplicate branches by courseTitle
    filtered = filtered
      .map((college) => {
        const uniqueCourses = new Map<string, ICollegeCourse>();
        college.courseVo.forEach((course) => {
          if (!uniqueCourses.has(course.courseTitle)) {
            uniqueCourses.set(course.courseTitle, course);
          }
        });
        return {
          ...college,
          courseVo: Array.from(uniqueCourses.values()),
        };
      })
      .filter((college) => college.courseVo.length > 0);

    setAllColleges(colleges);
    setFilteredColleges(filtered);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));

    console.log("Filtered Colleges (Unique Branches):", filtered);
  }, [
    collegesData,
    searchQuery,
    ownershipFilter,
    collegeFilter,
    courseFilter,
    cityFilter,
    counselling,
    state,
    caste,
    gender,
  ]);

  // Update the allCourses calculation to use college-specific latest year
 const allCourses = allColleges
   .flatMap((c) =>
     c.allCourses.map((course) => ({
       ...course,
       gender: course.gender === "" ? "Neutral" : course.gender || "Neutral",
       lastYearCutoffAiRank: course.lastYearCutoffAiRank,
       courseName: course.courseName,
       courseTitle: course.courseTitle,
       openingRank: course.openingRank,
       closingRank: course.closingRank,
       fee: course.fee,
       seats: course.seats,
       collegeId: course.collegeId,
       caste: course.caste,
       counsellingName: course.counsellingName,
       year: course.year,
       probability: "N/A",
     }))
   );
  console.log("All Colleges:", allColleges);
  console.log("Filtered Colleges:", filteredColleges);
  console.log("All Courses:", allCourses);

  const ownershipOptions = Array.from(
    new Set(allColleges.map((college) => college.ownerShip))
  ).map((ownerShip) => ({ label: ownerShip, value: ownerShip }));
  const collegeOptions = Array.from(
    new Set(allColleges.map((college) => college.collegeTitle))
  ).map((collegeTitle) => ({ label: collegeTitle, value: collegeTitle }));
  const courseOptions = Array.from(
    new Set(
      allColleges.flatMap((college) =>
        college.courseVo.map((course) => course.courseTitle)
      )
    )
  ).map((courseTitle) => ({ label: courseTitle, value: courseTitle }));
  const cityOptions = Array.from(
    new Set(allColleges.map((college) => college.cityName))
  )
    .filter((city) => city)
    .map((city) => ({ label: city, value: city }));

  const ownershipOptionsWithAll = [
    { label: "All", value: "" },
    ...ownershipOptions,
  ];
  const collegeOptionsWithAll = [
    { label: "All", value: "" },
    ...collegeOptions,
  ];
  const courseOptionsWithAll = [{ label: "All", value: "" }, ...courseOptions];
  const cityOptionsWithAll = [{ label: "All", value: "" }, ...cityOptions];

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedColleges = filteredColleges.slice(startIndex, endIndex);

  const transformedColleges: College[] = paginatedColleges.map((college) => ({
    collegeName: college.collegeName,
    collegeTitle: college.collegeTitle,
    ownerShip: college.ownerShip,
    collegeId: college.collegeId.toString(),
    courseVo: college.courseVo.map((course) => {
      const userRankNum = rank ? parseInt(rank, 10) : -1;
      const openingRank = course.openingRank !== -1 ? course.openingRank : -1;
      const closingRank = course.closingRank !== -1 ? course.closingRank : -1;

      let probability: string;
      if (userRankNum === -1 || openingRank === -1 || closingRank === -1) {
        probability = "N/A";
      } else if (userRankNum <= openingRank) {
        probability = "High";
      } else if (userRankNum >= openingRank && userRankNum <= closingRank) {
        probability = "Good";
      } else {
        probability = "Low";
      }

      return {
        lastYearCutoffAiRank: course.lastYearCutoffAiRank,
        courseName: course.courseName,
        courseTitle: course.courseTitle,
        openingRank: course.openingRank,
        closingRank: course.closingRank,
        probability,
        fee: course.fee,
        seats: course.seats,
        collegeId: course.collegeId,
        caste: course.caste,
        gender: course.gender,
        counsellingName: course.counsellingName,
        year: course.year,
      };
    }),
    location: college.stateName + ", " + college.cityName,
    nirfRank: "9",
    degree: "",
    allCourses: college.allCourses
  }));

  const cardData: CollegesData = { colleges: transformedColleges };

  console.log("Transformed Colleges:", transformedColleges);

  const summaryData = {
    toughChanceColleges: collegesData?.toughChanceColleges || 0,
    goodChanceColleges: collegesData?.goodChanceColleges || 0,
    getinChanceColleges: collegesData?.getinChanceColleges || 0,
    examName: examName,
  };

  const formatExamName = (slug: string) => {
    if (!slug) return "";
    return slug
      .split("-")
      .map((word) =>
        word.length > 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : ""
      )
      .join(" ");
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      listingContainerRef.current?.scrollIntoView();
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      listingContainerRef.current?.scrollIntoView();
    }
  };

  const handleNewPrediction = () => {
    navigate(`/college-predictor/${examName}`);
  };

  const handleSaveList = () => {
    const doc = new jsPDF("p", "pt", "a4");

    doc.setFontSize(14);
    doc.text("Predicted College List Report", 40, 30);

    const tableColumn = [
      { header: "College Name", dataKey: "collegeName" },
      { header: "Course Title", dataKey: "courseTitle" },
      { header: "Ownership", dataKey: "ownerShip" },
      { header: "City", dataKey: "cityName" },
      { header: "State", dataKey: "stateName" },
      { header: "Opening Rank", dataKey: "openingRank" },
      { header: "Closing Rank", dataKey: "closingRank" },
      { header: "Fee", dataKey: "fee" },
      { header: "Seats", dataKey: "seats" },
      { header: "Caste", dataKey: "caste" },
      { header: "Gender", dataKey: "gender" },
      { header: "Year", dataKey: "year" },
    ];

    const tableRows = filteredColleges.flatMap((college) =>
      college.courseVo.map((course) => ({
        collegeName: college.collegeName,
        courseTitle: course.courseTitle,
        ownerShip: college.ownerShip,
        cityName: college.cityName,
        stateName: college.stateName,
        openingRank: course.openingRank,
        closingRank: course.closingRank,
        fee: course.fee,
        seats: course.seats,
        caste: course.caste,
        gender: course.gender,
        year: course.year,
      }))
    );

    autoTable(doc, {
      columns: tableColumn,
      body: tableRows,
      startY: 40,
      styles: {
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
        valign: "top",
      },
      didDrawPage: () => {
        doc.setFontSize(10);
        doc.text(
          "Generated by MentorMentee Predictor",
          40,
          doc.internal.pageSize.height - 20
        );

        const pageNumber = doc.getCurrentPageInfo().pageNumber;
        const totalPages = doc.getNumberOfPages();
        doc.text(
          `Page ${pageNumber} of ${totalPages}`,
          doc.internal.pageSize.width - 100,
          doc.internal.pageSize.height - 20
        );
      },
    });

    doc.save("Predicted_Colleges_List.pdf");
  };

  

  const customSelectStyles: StylesConfig<any, false> = {
    control: (provided) => ({
      ...provided,
      borderRadius: "20px",
      border: "1px solid #d1d5db",
      boxShadow: "none",
      "&:hover": { borderColor: "#9ca3af" },
      backgroundColor: "white",
      padding: "0 10px",
      height: "40px",
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
      width: "100%",
    }),
    container: (provided) => ({
      ...provided,
      width: "auto",
      minWidth: "140px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6b7280",
      fontSize: "14px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#6b7280",
      fontSize: "14px",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      width: "auto",
      minWidth: "100%",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e5e7eb"
        : "white",
      color: state.isSelected ? "white" : "#1f2937",
      "&:active": { backgroundColor: "#2563eb" },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#6b7280",
      padding: "0 4px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: "0",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
  };

  const formattedExamName = formatExamName(examName || "");

  return (
    <div className="text-black font-inter py-12">
      <div
        ref={listingContainerRef}
        className="w-full px-4 sm:px-6 lg:px-8 flex flex-col gap-8"
      >
        <div className="flex flex-col md:flex-row gap-6 md:pb-5 justify-between">
          {/* Display Form Data Above Search */}
          <div className="text-left mb-6 text-blue">
            <h1 className="text-2xl font-bold text-blue">
              Early Rank & College Predictor 2025
            </h1>
            {formData && (
              <>
                <div className="flex flex-col gap-3.5">
                  <p className="text-xl mt-2">
                    Category - <strong>{formData.caste}</strong>
                  </p>
                  <p className="text-xl">
                    Predicted - <strong>{formattedExamName}</strong>
                  </p>
                  <p className="text-xl">
                    Rank - <strong>{formData.rank}</strong>
                  </p>
                  <p className="text-xl">
                    State - <strong>{formData.state}</strong>
                  </p>
                  <p className="text-xl">
                    Gender - <strong>{formData.gender}</strong>
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="w-full md:w-[448px]">
            <PredictedRankCard counselling={counselling} rank={rank} />{" "}
          </div>
        </div>
        <div className="w-full flex flex-col gap-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="relative w-full sm:max-w-md">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search colleges..."
                  className="w-full h-10 p-3 pl-9 text-sm text-black bg-white border border-[#A3A2AC] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Search colleges"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-4 justify-start sm:justify-end flex-wrap">
                <button
                  onClick={handleNewPrediction}
                  className="p-2 border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 text-sm"
                  aria-label="New Prediction"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  New Prediction
                </button>
                <button
                  onClick={handleSaveList}
                  className="p-2 border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  aria-label="Save this List"
                >
                  Save this List
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex flex-col">
                <label className="block mb-2 text-sm">Ownership</label>
                <Select
                  isMulti={false}
                  options={ownershipOptionsWithAll}
                  value={ownershipOptionsWithAll.find(
                    (opt) => opt.value === ownershipFilter
                  )}
                  onChange={(selected) =>
                    setOwnershipFilter(selected ? selected.value : "")
                  }
                  placeholder="Select ownership"
                  styles={customSelectStyles}
                />
              </div>
              <div className="flex flex-col">
                <label className="block mb-2 text-sm">College</label>
                <Select
                  isMulti={false}
                  options={collegeOptionsWithAll}
                  value={collegeOptionsWithAll.find(
                    (opt) => opt.value === collegeFilter
                  )}
                  onChange={(selected) =>
                    setCollegeFilter(selected ? selected.value : "")
                  }
                  placeholder="Select college"
                  styles={customSelectStyles}
                />
              </div>
              <div className="flex flex-col">
                <label className="block mb-2 text-sm">Course</label>
                <Select
                  isMulti={false}
                  options={courseOptionsWithAll}
                  value={courseOptionsWithAll.find(
                    (opt) => opt.value === courseFilter
                  )}
                  onChange={(selected) =>
                    setCourseFilter(selected ? selected.value : "")
                  }
                  placeholder="Select course"
                  styles={customSelectStyles}
                />
              </div>
              <div className="max-w-36 w-full">
                <label className="block mb-2 text-sm">City</label>
                <Select
                  isMulti={false}
                  options={cityOptionsWithAll}
                  value={cityOptionsWithAll.find(
                    (opt) => opt.value === cityFilter
                  )}
                  onChange={(selected) =>
                    setCityFilter(selected ? selected.value : "")
                  }
                  placeholder="Select city"
                  styles={customSelectStyles}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <ReportDownload filteredColleges={filteredColleges} />
            <h3 className="text-base">
              Showing{" "}
              <strong>
                {startIndex + 1} -{" "}
                {Math.min(
                  startIndex + paginatedColleges.length,
                  filteredColleges.length
                )}
              </strong>{" "}
              of <strong>{filteredColleges.length}</strong> Colleges in{" "}
              <strong>
                {counselling || "Joint Seat Allocation Authority"}
              </strong>
            </h3>

            <PredictorResultCard data={cardData} userRank={rank} />
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-4 text-base">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`rounded-full w-10 h-10 max-w-10 text-sm flex flex-col items-center justify-center ${
                currentPage === 1
                  ? "bg-gray-300"
                  : "bg-blue text-white hover:bg-blue"
              }`}
            >
              <EpRight className="rotate-180" />
            </button>
            <span className="text-base">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`rounded-full w-10 h-10 max-w-10 text-sm flex flex-col items-center justify-center ${
                currentPage === totalPages
                  ? "bg-gray-300"
                  : "bg-blue text-white hover:bg-blue"
              }`}
            >
              <EpRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollegePredictorResult;
